// Vínculos entre registros e tempos de ponta a ponta (ADR 0066; ADR 0061, seção 5, regra 11).
import { z } from 'zod';
import type { Pause } from './sla.ts';

export const RECORD_LINK_KINDS = ['originated', 'forwarded', 'depends_on', 'related'] as const;
export type RecordLinkKind = (typeof RECORD_LINK_KINDS)[number];
/** Só estes formam o caso (processo de ponta a ponta). */
export const CASE_LINK_KINDS = ['originated', 'forwarded'] as const satisfies RecordLinkKind[];

const recordType = z.string().regex(/^[a-z_]+\.[a-z_]+$/);

export const recordLinkInput = z
  .object({
    sourceType: recordType,
    sourceId: z.uuid(),
    targetType: recordType,
    targetId: z.uuid(),
    kind: z.enum(RECORD_LINK_KINDS),
    primary: z.boolean().default(false),
    linkedAt: z.coerce.date().optional(),
  })
  .refine((v) => !v.primary || v.kind === 'originated', {
    message: 'only_originated_can_be_primary',
    path: ['primary'],
  })
  .refine((v) => v.sourceType !== v.targetType || v.sourceId !== v.targetId, {
    message: 'self_link',
    path: ['targetId'],
  });
export type RecordLinkInput = z.input<typeof recordLinkInput>;

export const recordLinkUndo = z.object({ reason: z.string().trim().min(3).max(500) });

/** Um relógio do caso: início, desfecho (ou aberto) e pausas autorizadas. */
export interface CaseClock {
  start: number;
  end: number | null;
  pauses: Pause[];
}

export interface CaseTimes {
  start: number;
  end: number;
  /** Todos os relógios com desfecho. */
  closed: boolean;
  elapsed: number;
  effective: number;
  justifiedWait: number;
  betweenAreas: number;
}

type Interval = [number, number];

function union(list: Interval[]): Interval[] {
  const sorted = list.filter(([a, b]) => b > a).sort((x, y) => x[0] - y[0]);
  const out: Interval[] = [];
  for (const [a, b] of sorted) {
    const last = out[out.length - 1];
    if (last && a <= last[1]) last[1] = Math.max(last[1], b);
    else out.push([a, b]);
  }
  return out;
}

const measure = (list: Interval[]) => list.reduce((s, [a, b]) => s + (b - a), 0);

/** Partes de `a` fora de `b` (as duas já unidas). */
function subtract(a: Interval[], b: Interval[]): Interval[] {
  const out: Interval[] = [];
  for (const [start, end] of a) {
    let cursor = start;
    for (const [bs, be] of b) {
      if (be <= cursor || bs >= end) continue;
      if (bs > cursor) out.push([cursor, bs]);
      cursor = Math.max(cursor, be);
    }
    if (cursor < end) out.push([cursor, end]);
  }
  return out;
}

/**
 * Tempos do caso em tempo corrido (regra 11): transcorrido, efetivo (algum relógio correndo),
 * espera justificada (nenhum correndo e algum pausado) e espera entre áreas (nenhum aberto).
 * Relógios paralelos nunca se somam. Sem relógios, não há tempos.
 */
export function caseTimes(clocks: CaseClock[], now: number): CaseTimes | null {
  if (!clocks.length) return null;
  const closed = clocks.every((c) => c.end !== null);
  const start = Math.min(...clocks.map((c) => c.start));
  const end = closed ? Math.max(...clocks.map((c) => c.end!)) : now;
  const running: Interval[] = [];
  const paused: Interval[] = [];
  for (const c of clocks) {
    const stop = Math.min(c.end ?? now, end);
    const pauses = union(
      c.pauses.map((p) => [Math.max(p.start, c.start), Math.min(p.end, stop)] as Interval),
    );
    running.push(...subtract([[c.start, stop]], pauses));
    paused.push(...pauses);
  }
  const run = union(running);
  const effective = measure(run);
  const justifiedWait = measure(subtract(union(paused), run));
  const elapsed = Math.max(0, end - start);
  return {
    start,
    end,
    closed,
    elapsed,
    effective,
    justifiedWait,
    betweenAreas: Math.max(0, elapsed - effective - justifiedWait),
  };
}
